import { FC, useState, useEffect, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBackIcon } from '../../../../../components/Icons/ArrowBackIcon';
import { CloseIcon } from '../../../../../components/Icons/CloseIcon';

import styles from './TravelContactsPanel.module.scss';
import { TripStop } from '../../../../../types/trip.types';
import { useGetTravelContactsQuery, useCreateTravelContactMutation } from '../../../../../store/services/tripsApi';
import CustomButton from '../../../../../common/CustomButton';
import CustomInput from '../../../../../common/CustomInput';
import { AddPhoneIcon, AddEmailIcon, AddLinkIcon } from '../../../../../components/Icons';

interface TravelContactsPanelProps {
  activity: TripStop;
  tripId: number;
  onClose: () => void;
  onRefetchTrip?: () => void;
}

type ContactTab = 'phone' | 'email' | 'link';

const TravelContactsPanel: FC<TravelContactsPanelProps> = ({ activity, tripId, onClose, onRefetchTrip }) => {
  const [forceRefresh, setForceRefresh] = useState(0);

  const {
    data: travelContacts,
    isLoading: isContactsLoading,
    refetch: refetchContacts,
  } = useGetTravelContactsQuery(activity.id.toString(), {
    refetchOnMountOrArgChange: true,
  });

  const [createTravelContact] = useCreateTravelContactMutation();

  const [activeTab, setActiveTab] = useState<ContactTab>('phone');
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    link: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const refreshData = async () => {
      try {
        await refetchContacts();
        setForceRefresh(prev => prev + 1);
      } catch (error) {}
    };

    refreshData();
  }, [activity.id, refetchContacts]);

  useEffect(() => {
    setFormErrors({});
  }, [activeTab]);

  const handleInputChange = (field: string, value: string) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!contactForm.name.trim()) {
      errors.name = 'Name is required';
    }

    if (activeTab === 'phone') {
      if (!contactForm.phone.trim()) {
        errors.phone = 'Phone is required';
      } else {
        const phoneRegex = /^\+?[1-9]\d{9,14}$/;
        const cleanedPhone = contactForm.phone.replace(/[\s\-()]/g, '');
        if (!phoneRegex.test(cleanedPhone)) {
          errors.phone = 'Invalid phone number format';
        }
      }
    } else if (activeTab === 'email') {
      if (!contactForm.email.trim()) {
        errors.email = 'Email is required';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contactForm.email)) {
          errors.email = 'Invalid email format';
        }
      }
    } else if (activeTab === 'link') {
      if (!contactForm.link.trim()) {
        errors.link = 'Link is required';
      } else {
        const urlRegex = /^https?:\/\/[^\s]+$/;
        if (!urlRegex.test(contactForm.link)) {
          errors.link = 'Link must start with http:// or https://';
        }
      }
    }

    const hasPhone = contactForm.phone.trim();
    const hasEmail = contactForm.email.trim();
    const hasLink = contactForm.link.trim();

    if (hasPhone && activeTab !== 'phone') {
      const phoneRegex = /^\+?[1-9]\d{9,14}$/;
      const cleanedPhone = contactForm.phone.replace(/[\s\-()]/g, '');
      if (!phoneRegex.test(cleanedPhone)) {
        errors.phone = 'Invalid phone number format';
      }
    }

    if (hasEmail && activeTab !== 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactForm.email)) {
        errors.email = 'Invalid email format';
      }
    }

    if (hasLink && activeTab !== 'link') {
      const urlRegex = /^https?:\/\/[^\s]+$/;
      if (!urlRegex.test(contactForm.link)) {
        errors.link = 'Link must start with http:// or https://';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const contactData: any = {
        name: contactForm.name.trim(),
        phone_number: contactForm.phone.trim() || null,
        email: contactForm.email.trim() || null,
        link: contactForm.link.trim() || null,
      };

      await createTravelContact({
        tripStopId: activity.id.toString(),
        contactData,
      }).unwrap();

      setContactForm({ name: '', phone: '', email: '', link: '' });

      await refetchContacts();
      onRefetchTrip?.();
      setForceRefresh(prev => prev + 1);
    } catch (error) {
      setFormErrors({ general: 'Failed to create contact. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setContactForm({ name: '', phone: '', email: '', link: '' });
    setFormErrors({});
  };

  const handleTabButtonClick = (tab: ContactTab) => {
    setActiveTab(tab);
  };

  const savedContacts = useMemo(() => {
    if (isContactsLoading || !travelContacts) return [];

    const contacts: string[] = [];
    const userContacts = travelContacts.filter(contact => contact.added_by_type === 'user');

    userContacts.forEach(contact => {
      if (contact.phone_number) {
        contacts.push(`${contact.name} - Phone ${contact.phone_number}`);
      }
      if (contact.email) {
        contacts.push(`${contact.name} - Email ${contact.email}`);
      }
      if (contact.link) {
        contacts.push(`${contact.name} - ${contact.link}`);
      }
    });

    return contacts;
  }, [isContactsLoading, travelContacts, forceRefresh]);

  const foundContacts = useMemo(() => {
    if (isContactsLoading || !travelContacts) return [];

    const contacts: string[] = [];
    const systemContacts = travelContacts.filter(contact => contact.added_by_type === 'system');

    systemContacts.forEach(contact => {
      if (contact.phone_number) {
        contacts.push(`${contact.name} - Phone ${contact.phone_number}`);
      }
      if (contact.email) {
        contacts.push(`${contact.name} - Email ${contact.email}`);
      }
      if (contact.link) {
        contacts.push(`${contact.name} - ${contact.link}`);
      }
    });

    return contacts;
  }, [isContactsLoading, travelContacts, forceRefresh]);

  return (
    <Box className={styles.travelContactsPanel}>
      <Box className={styles.header}>
        <Box className={styles.headerLeft}>
          <IconButton
            onClick={onClose}
            className={styles.backButton}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h7Bold"
            className={styles.title}
          >
            Travel Contacts
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          className={styles.closeButton}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={styles.content}>
        {isContactsLoading && (
          <Box className={styles.loadingContainer}>
            <Typography
              variant="h11"
              sx={{ color: '#5e6a81', textAlign: 'center' }}
            >
              Loading contacts...
            </Typography>
          </Box>
        )}

        {!isContactsLoading && (
          <>
            <Box className={styles.section}>
              <Typography
                variant="h9Bold"
                className={styles.sectionTitle}
              >
                Saved by You
              </Typography>
              <Typography
                variant="h11"
                className={styles.sectionSubtitle}
                sx={{ color: '#5E6A81' }}
              >
                Contacts you've added manually
              </Typography>

              <Box className={styles.savedContacts}>
                {savedContacts.length > 0 ? (
                  savedContacts.map((contact, index) => (
                    <Typography
                      key={index}
                      variant="h11"
                      className={`${styles.contactText} ${contact.includes('http') ? styles.linkText : ''}`}
                      onClick={
                        contact.includes('http') ? () => window.open(contact.split(' - ')[1], '_blank') : undefined
                      }
                      sx={{wordBreak: 'break-all'}}
                    >
                      {contact}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    variant="h11"
                    className={styles.emptyText}
                  >
                    No contacts saved yet
                  </Typography>
                )}
              </Box>
            </Box>

            <Box className={styles.section}>
              <Typography
                variant="h9Bold"
                className={styles.sectionTitle}
              >
                Found for You
              </Typography>
              <Typography
                variant="h11"
                className={styles.sectionSubtitle}
                sx={{ color: '#5E6A81' }}
              >
                Contacts we've found for this trip
              </Typography>

              <Box className={styles.foundContacts}>
                {foundContacts.length > 0 ? (
                  foundContacts.map((contact, index) => (
                    <Typography
                      key={index}
                      variant="h11"
                      className={`${styles.contactText} ${contact.includes('http') ? styles.linkText : ''}`}
                      onClick={
                        contact.includes('http') ? () => window.open(contact.split(' - ')[1], '_blank') : undefined
                      }
                    >
                      {contact}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    variant="h11"
                    className={styles.emptyText}
                  >
                    No contacts found for this trip
                  </Typography>
                )}

                <Box className={styles.contactTabs}>
                  <button
                    className={`${styles.tabButton} ${activeTab === 'phone' ? styles.active : ''}`}
                    onClick={() => handleTabButtonClick('phone')}
                  >
                    <AddPhoneIcon
                      size={24}
                      className={styles.tabIcon}
                      color={activeTab === 'phone' ? '#0098F0' : '#5E6A81'}
                    />
                    <Typography variant="h12" color="#222B3A">Add Phone</Typography>
                  </button>

                  <button
                    className={`${styles.tabButton} ${activeTab === 'email' ? styles.active : ''}`}
                    onClick={() => handleTabButtonClick('email')}
                  >
                    <AddEmailIcon
                      size={24}
                      className={styles.tabIcon}
                      color={activeTab === 'email' ? '#0098F0' : '#5E6A81'}
                    />
                    <Typography variant="h12" color="#222B3A">Add Mail</Typography>
                  </button>

                  <button
                    className={`${styles.tabButton} ${activeTab === 'link' ? styles.active : ''}`}
                    onClick={() => handleTabButtonClick('link')}
                  >
                    <AddLinkIcon
                      size={24}
                      className={styles.tabIcon}
                      color={activeTab === 'link' ? '#0098F0' : '#5E6A81'}
                    />
                    <Typography variant="h12" color="#222B3A">Add Link</Typography>
                  </button>
                </Box>
              </Box>
            </Box>
          </>
        )}

        <Box className={styles.addContactForm}>
          <Box className={styles.formFields}>
            <CustomInput
              inputName="name"
              inputLabel="Name"
              value={contactForm.name}
              onChange={e => handleInputChange('name', e.target.value)}
              error={formErrors.name}
              placeholder="Reception"
            />

            {activeTab === 'phone' && (
              <CustomInput
                inputName="phone"
                inputLabel="Phone"
                value={contactForm.phone}
                onChange={e => handleInputChange('phone', e.target.value)}
                error={formErrors.phone}
                placeholder="+48 22 630 6306"
              />
            )}

            {activeTab === 'email' && (
              <CustomInput
                inputName="email"
                inputLabel="Email"
                inputType="email"
                value={contactForm.email}
                onChange={e => handleInputChange('email', e.target.value)}
                error={formErrors.email}
                placeholder="contact@example.com"
              />
            )}

            {activeTab === 'link' && (
              <CustomInput
                inputName="link"
                inputLabel="Link"
                value={contactForm.link}
                onChange={e => handleInputChange('link', e.target.value)}
                error={formErrors.link}
                placeholder="https://..."
              />
            )}

            {formErrors.general && (
              <Typography
                variant="h12"
                sx={{ color: '#C8102E', textAlign: 'center' }}
              >
                {formErrors.general}
              </Typography>
            )}
          </Box>

          <Box className={styles.formActions}>
            <CustomButton
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
              customClass={styles.cancelButton}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleAdd}
              disabled={isSubmitting}
              customClass={styles.addButton}
            >
              {isSubmitting ? 'Adding...' : 'Add'}
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TravelContactsPanel;
